import { stringToDocumentFragment } from "@otto-ec/global-resources/dom";
import { executeInlineScripts } from "@otto-ec/global-resources/hardcore";
import {
  flatMap,
  pipe,
  succeed,
  tap,
  catchType,
  allSync,
  tapSpread,
  fromNullable,
  tapLeft,
  tapRight,
  fromCondition,
} from "@otto-ec/assets-core-utils/nano-effect";
import { untrack } from "svelte";
import { sheetScope } from "../utils/props";
import { getPropsFromContent, parseLegacyContent, updatePropsFromContent } from "./parser";
import type { OcSheetV1Props } from "../SheetV1.types.g";
import { LoadError } from "./loadError";
import { fetchFromUrl } from "./fetchUtils";
import { dispatchSheetEvent } from "../event/event.dispatch";
import {
  SHEET_EVENT_CONTENT_APPLY,
  SHEET_EVENT_CONTENT_DISCARD,
  SHEET_EVENT_CONTENT_ERROR,
  SHEET_EVENT_CONTENT_FETCH,
  SHEET_EVENT_CONTENT_PARSE,
} from "../event/event.consts";
import { assertContentURL } from "./assert";
import type { OcSheetV1InstanceState } from "../instance/stack.svelte";

const log = /*           */ sheetScope.scope("external-content");

/*                                                                        */
export function useExternalContent(
  host: HTMLOcSheetV1Element,
  init: {
    /*                                                         */
    url: () => string | undefined;

    /*                                               */
    allowedCodes: () => number[];

    /*                                                       */
    invalidateOnInactive: () => boolean;
  },

  instanceState: OcSheetV1InstanceState,
) {
  const state = $state({
    /*                                                                                  */
    validUrl: null as string | null,

    /*                                                                                */
    rawContent: null as string | null,
    /*                                                                 */
    rawError: null as Error | LoadError | null,

    /*                                                                                    */
    loadedContent: null as DocumentFragment | null,
    /*                                                                                        */
    errorContent: null as null | DocumentFragment,

    /*                                                                              */
    externalOptions: null as Partial<OcSheetV1Props> | null,
    /*                                                                 */
    externalContent: null as DocumentFragment | null,

    /*                                                                       */
    isApplied: false,
  });

  /**
 *
 *
 */
  let currentSheetDom: DocumentFragment | null = null;

  /*                                                                    */
  /*                                                                       */
  $effect(() => {
    const urlProp = init.url();

    /*                                                        */
    if (!urlProp) return;

    pipe(
      assertContentURL(urlProp),

      /*                                                         */
      /*                             */
      tap((url) => {
        state.rawContent = null;
        state.rawError = null;

        /*                                                                                */
        state.validUrl = import.meta.env.DEV && import.meta.env.STORYBOOK ? `/dev${url}` : url;
      }),

      /*                       */
      tapLeft((error) => {
        if (import.meta.env.DEV) log.error("Error validating external content url", error);
        state.validUrl = null;
        state.rawError = error;
      }),
    );
  });

  /*                                                                              */
  /*                                                                                                 */
  $effect(() => {
    /*                                                    */
    if (!init.invalidateOnInactive() || !state.validUrl) return;
    /*                                    */
    if (instanceState.isActive || state.rawContent === null) return;

    dispatchSheetEvent(SHEET_EVENT_CONTENT_DISCARD, host, {});

    /*                                           */
    state.externalContent = null;
    state.loadedContent = null;
    state.rawContent = null;
    state.rawError = null;
  });

  /*                                                                               */
  /*                                                                                        */
  $effect(() => {
    /*                         */
    if (state.rawError !== null || state.rawContent !== null || !currentSheetDom) return;

    /*                                                                   */
    state.isApplied = false;
    host.replaceChildren(currentSheetDom);
  });

  /*                                                           */
  /*                                                  */
  $effect(() => {
    const validUrl = state.validUrl;
    if (!instanceState.isActive || !validUrl || state.rawContent !== null) return;

    pipe(
      fetchFromUrl(validUrl, untrack(init.allowedCodes)),

      /*                                                              */
      tap((raw) => {
        state.isApplied = false;

        /*                                           */
        dispatchSheetEvent(SHEET_EVENT_CONTENT_FETCH, host, { url: validUrl, raw });

        state.rawContent = raw;

        /*                                              */
        state.loadedContent = stringToDocumentFragment(state.rawContent);
      }),

      /*                                      */
      tapLeft((error) => {
        state.rawError = error;
      }),
    );
  });

  /*                                                           */
  $effect(() => {
    pipe(
      /*                                    */
      fromCondition(!state.rawError, null, state.rawError!),

      /*                           */
      tapRight((nullValue) => {
        state.errorContent = nullValue;
      }),

      /*                                                  */
      /*                                              */
      tapLeft((error) => {
        log.error("Error loading external content", error);

        dispatchSheetEvent(SHEET_EVENT_CONTENT_ERROR, host, {
          raw: state.rawContent ?? undefined,
          url: init.url(),
          error,
        });
      }),

      /*                                 */
      catchType(LoadError, (error) => {
        if (error.errorContent) {
          /*                                      */
          state.errorContent = stringToDocumentFragment(error.errorContent);
        }
        return succeed(null);
      }),
    );
  });

  /*                                               */
  $effect(() => {
    pipe(
      /*                                   */
      fromNullable(state.loadedContent ?? state.errorContent),

      /*                               */
      flatMap((content) => allSync([getPropsFromContent(content), parseLegacyContent(content)])),

      /*                                                              */
      tapSpread((externalOptions, externalContent) => {
        dispatchSheetEvent(SHEET_EVENT_CONTENT_PARSE, host, {
          options: externalOptions,
          parsed: externalContent,
          raw: untrack(() => state.rawContent!),
          url: untrack(() => state.validUrl!),
        });

        state.externalOptions = externalOptions;
        state.externalContent = externalContent;
      }),

      /*                                                             */
      tapLeft((empty) => {
        state.externalOptions = empty;
        state.externalContent = empty;
      }),
    );
  });

  /*                                 */
  $effect(() => {
    if (!state.externalOptions) return;
    /*                         */
    updatePropsFromContent(host, state.externalOptions);
  });

  /*                                         */
  $effect(() => {
    /*                                                      */
    /*                                                                         */
    /*                                */
    if (!state.externalContent || !instanceState.isActive || state.isApplied) return;

    /*                                            */
    if (import.meta.env.DEV) log.debug("Applying external content to sheet", state.externalContent);
    if (!currentSheetDom) {
      currentSheetDom = new DocumentFragment();
    }
    currentSheetDom.replaceChildren(...host.childNodes);

    /*                         */
    host.appendChild(state.externalContent);
    executeInlineScripts(host);

    /*                                                                 */
    /*                                          */
    state.isApplied = true;

    dispatchSheetEvent(SHEET_EVENT_CONTENT_APPLY, host, {});
  });

  return state;
}
